/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IBuyboxAddToCartViewProps, IBuyboxAddToWishlistViewProps, IBuyboxKeyInPriceViewProps, IBuyboxProductConfigureDropdownViewProps, IBuyboxProductConfigureViewProps, IBuyboxProductQuantityViewProps } from '../c-buybox/components/c-buyboxInterface';
import { IQuickviewViewProps } from './c-quickview';
import { ICQuickviewProps } from "./c-quickview.props.autogenerated";

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderAddToWishlist = (addToWishlist: IBuyboxAddToWishlistViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToWishlist;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;
    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderQuickViewPopup = (props: IQuickviewViewProps): JSX.Element => {
    const { ModalContainer, ModalHeaderContainer, ModalFooterContainer, ModalHeaderContent, ModalBodyContainer, addToWishlist, addToCart,
        cartContainerProps } = props;
    return (
        <Module {...ModalContainer}>
            <Node {...ModalHeaderContainer}>
                {ModalHeaderContent}
            </Node>
            <Node {...ModalBodyContainer}>
                {_renderBodyContent(props)}
            </Node>
            <Node {...ModalFooterContainer}>
                <Node {...cartContainerProps}>
                    {addToCart && _renderAddToCart(addToCart)}
                    {addToWishlist && _renderAddToWishlist(addToWishlist)}
                </Node>
            </Node>
        </Module>
    );
};

const _renderQuantity = (quantity: IBuyboxProductQuantityViewProps, quantityLimitsMessages: React.ReactNode): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {input}
            {quantityLimitsMessages}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
            </Node>
            {input}
        </Node>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns, messageBox } = configure;

    return (
        <Node {...ContainerProps}>
            {dropdowns.map(_renderConfigureDropdown)}
            {messageBox}
        </Node>
    );
};

const _renderBodyContent = (props: IQuickviewViewProps & ICQuickviewProps<{}>): JSX.Element => {
    const { title, price, keyInPrice, quantity, configure, inventoryLabel, quantityLimitsMessages, seeDetailsbutton, loading,
        ProductInfoContainerProps, MediaGalleryContainerProps, CarouselProps, unitOfMeasure } = props;
    if (loading) {
        return (
            <>
                {loading}
            </>
        );
    }

    if (typeof window !== 'undefined') {
        React.useEffect(() => {

            const url = window.location.href;
            const script = document.createElement('script');
            url.indexOf("dynamics.com/uk") > -1 ? script.src = "https://apps.bazaarvoice.com/deployments/peruvianconnectionuk/main_site/staging/en_GB/bv.js"
            : url.indexOf("dynamics.com/de") > -1 ? script.src = "https://apps.bazaarvoice.com/deployments/peruvianconnectionde/main_site/staging/de_DE/bv.js"
            : url.indexOf("peruvianconnection.com") > -1 ? script.src = "https://apps.bazaarvoice.com/deployments/peruvianconnection/main_site/production/en_US/bv.js"
            : url.indexOf("peruvianconnection.co.uk") > -1 ? script.src = "https://apps.bazaarvoice.com/deployments/peruvianconnectionuk/main_site/production/en_GB/bv.js"
            : url.indexOf("peruvianconnection.de") > -1 ? script.src = "https://apps.bazaarvoice.com/deployments/peruvianconnectionde/main_site/production/de_DE/bv.js"
            : script.src = "https://apps.bazaarvoice.com/deployments/peruvianconnection/main_site/staging/en_US/bv.js"
            script.async = true;
            document.getElementsByClassName("ms-quickView__content")[0].appendChild(script);
            return () => {
                document.getElementsByClassName("ms-quickView__content")[0].removeChild(script);
            }
    
        }, [])
    }

    return (
        <>
            <Node {...MediaGalleryContainerProps}>
                <Node {...CarouselProps} />
                {/* {props.mediaGallery} */}
            </Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                {price}
                {unitOfMeasure}
                {_renderBVRatingSummary(props)}
                {/* {rating} */}
                {configure && _renderConfigure(configure)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {quantity && _renderQuantity(quantity, quantityLimitsMessages)}
                {seeDetailsbutton}
                {inventoryLabel}
            </Node>
        </>
    );

};

/**
 * Functional component which renders quick view button and popup based by the given props file.
 * @param {IQuickviewViewProps} props - Configuration for the quick view component.
 * @returns {React.FC<IQuickviewViewProps>} - Functional component of the quick view.
 */
export const QuickViewFunctionalComponent: React.FC<IQuickviewViewProps & ICQuickviewProps<{}>> = (props: IQuickviewViewProps & ICQuickviewProps<{}>) => {
    const { quickView, quickViewButton, isModalOpen, isMobileDevice } = props;
    return (
        <Module {...quickView}>
            {!isMobileDevice && quickViewButton}
            {isModalOpen && _renderQuickViewPopup(props)}
        </Module>
    );
};

const _renderBVRatingSummary = (props: IQuickviewViewProps & ICQuickviewProps<{}>) => {
    
    return(
        <div className="bv-rating_summary" data-bv-product-id={props.productDetails?.product?.ItemId} data-bv-show="rating_summary"></div>
    );
}

export default QuickViewFunctionalComponent;
